// Module 3 — Well Profile storage layer.
//
// Storage key: rsc.wells.v1
//
// Contract:
//   * createWellProfile(payload)   — assigns id + createdAt, validates, persists
//   * getWellProfile(id)           — returns one record (or null)
//   * listWellProfiles()           — returns all active records, newest first
//   * updateWellProfile(id, patch) — merges, re-validates, persists
//   * deleteWellProfile(id)        — hard delete (caller is responsible for any
//                                    downstream cleanup; today child containers
//                                    are inline so there are no orphans).
//
// UWI uniqueness is enforced HERE because we need to see siblings.
//
// Deleting a well does NOT touch Reference Libraries — Wells only *reference*
// library records via *RefId fields. The libraries remain pristine.

import { emptyWellProfile, validateWellProfile, validateWellProfileSection, migrateLegacyWellProfile } from "./schema";
import { scoped } from "@/lib/accountScope";

export const WELL_STORAGE_KEY = "rsc.wells.v1";

const _now = () => new Date().toISOString();

const _genId = () =>
  `well_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const _safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

function _readAll() {
  const raw = _safeParse(scoped.getItem(WELL_STORAGE_KEY), []);
  // Migrate every row on the way out — keeps the in-app shape current even
  // when the user has wells that were persisted by an older pass.
  return Array.isArray(raw) ? raw.map(migrateLegacyWellProfile) : [];
}
function _writeAll(rows) { scoped.setItem(WELL_STORAGE_KEY, JSON.stringify(rows)); }

function _normUwi(u) { return String(u || "").trim().toUpperCase(); }

// `sectionScoped`, when passed, is the sectionKey being saved (follow-up,
// ticket 237121). A per-section Save must only be blocked by that
// section's own bad data — not by an unrelated field elsewhere in the
// record (there is exactly one cross-section rule: offshore ⇒ positive
// water depth, see validateWellProfileSection). Every OTHER caller of
// createWellProfile/updateWellProfile omits this option and keeps the
// exact prior behaviour: full whole-record validateWellProfile as the
// persistence gate. UWI uniqueness is a real cross-record invariant, so
// it is always enforced regardless of sectionScoped.
function _validateForPersist(rec, all, ignoreId, sectionScoped) {
  const v = sectionScoped
    ? validateWellProfileSection(rec, sectionScoped)
    : validateWellProfile(rec);
  if (!v.ok) {
    const e = new Error("Well profile failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
  // UWI uniqueness (case-insensitive, ignoring blanks).
  const uwi = _normUwi(rec.uwi);
  if (uwi) {
    const dup = all.find((r) => r.id !== ignoreId && _normUwi(r.uwi) === uwi);
    if (dup) {
      const e = new Error(`UWI / API "${rec.uwi}" is already used by well "${dup.wellName}".`);
      e.validationErrors = [e.message];
      throw e;
    }
  }
}

/** Create a new Well Profile. Returns the persisted record. */
export function createWellProfile(payload, opts) {
  const all = _readAll();
  const base = { ...emptyWellProfile(), ...payload };
  // Ensure `children` is the full container (don't let a caller drop hooks).
  base.children = { ...emptyWellProfile().children, ...(payload?.children || {}) };
  base.id = _genId();
  base.createdAt = _now();
  base.updatedAt = base.createdAt;
  base.isActive = true;
  _validateForPersist(base, all, null, opts?.sectionScoped);
  all.push(base);
  _writeAll(all);
  return base;
}

/** Read a single Well Profile by id. Returns null if missing. */
export function getWellProfile(id) {
  return _readAll().find((r) => r.id === id) || null;
}

/**
 * List Well Profiles. Newest-updated first.
 *
 * Options:
 *   includeInactive: false  — when true, returns soft-deleted records too.
 */
export function listWellProfiles({ includeInactive = false } = {}) {
  const all = _readAll();
  const filtered = includeInactive ? all : all.filter((r) => r.isActive !== false);
  return filtered
    .slice()
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

/** Patch + re-validate + persist. Returns the updated record. */
export function updateWellProfile(id, patch, opts) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Well profile ${id} not found.`);
  const merged = {
    ...all[i],
    ...patch,
    id: all[i].id,
    createdAt: all[i].createdAt,
    children: { ...all[i].children, ...(patch?.children || {}) },
  };
  merged.updatedAt = _now();
  _validateForPersist(merged, all, id, opts?.sectionScoped);
  all[i] = merged;
  _writeAll(all);
  return merged;
}

/**
 * Hard-delete a Well Profile by id.
 *
 * IMPORTANT: This DOES NOT touch any Reference Library record. Wells only
 * carry *RefId fields into the libraries; deleting a well is a clean,
 * self-contained operation.
 */
export function deleteWellProfile(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  return all.length - next.length; // count removed
}

/** Test helper — wipe all wells. NEVER call from app code. */
export function _clearAllWellProfiles() {
  scoped.removeItem(WELL_STORAGE_KEY);
}
